import React, { useState, useRef, useEffect } from 'react';
import { MdFilterList, MdVisibility, MdAdd, MdPerson, MdEdit } from 'react-icons/md';
import ReactPaginate from 'react-paginate';
import TableLoadingWrapper from './TableLoadingWrapper';
import '../../styles/views.css';

const UsersView = ({
  pagedFilteredData,
  searchTerm,
  setSearchTerm,
  pageCount,
  handlePageClick,
  currentPage,
  totalRows,
  itemsPerPage,
  statusFilter,
  setStatusFilter,
  loading,
  userRole,
  userMmdaId,
  setViewUser,
  setIsViewUserModalOpen,
  setIsAddUserModalOpen,
  setEditUser,
  setIsEditUserModalOpen,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen width for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [openPopupIndex, setOpenPopupIndex] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRefs = useRef({});

  const handleActionClick = (event, index) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenPopupIndex(openPopupIndex === index ? null : index);
  };

  const closePopup = () => {
    setOpenPopupIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openPopupIndex !== null && popupRefs.current[openPopupIndex] && 
          !popupRefs.current[openPopupIndex].contains(event.target)) {
        closePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopupIndex]);

  const startIndex = currentPage * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRows);

  return (
    <>
      {/* Search and Filter Controls */}
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        gap: isMobile ? '12px' : '10px',
        flexWrap: isMobile ? 'nowrap' : 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search regional users..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: isMobile ? '100%' : '300px',
            padding: '8px 12px',
            borderRadius: 4,
            border: '1px solid #ccc',
            fontSize: isMobile ? 14 : 16,
            outline: 'none',
          }}
        />

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '8px' : '10px',
          width: isMobile ? '100%' : 'auto'
        }}>
          <button
            onClick={() => setIsAddUserModalOpen(true)}
            style={{
              padding: isMobile ? '8px 12px' : '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <MdAdd size={18} />
            {isMobile ? 'Create Officer' : 'Create Field Officer'}
          </button>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            width: isMobile ? '100%' : 'auto'
          }}>
            <MdFilterList size={20} style={{ color: '#666' }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: isMobile ? 13 : 14,
                outline: 'none',
                backgroundColor: 'white',
                width: isMobile ? '100%' : 'auto',
                minWidth: isMobile ? '100%' : '120px'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div style={{
            fontSize: isMobile ? 12 : 14,
            color: '#666',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Showing {startIndex + 1}-{endIndex} of {totalRows} regional users
          </div>
        </div>
      </div>

      {/* Regional Users Table */}
      <TableLoadingWrapper loading={loading}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Full Name</th>

                <th>Status</th>

                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedFilteredData.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    cursor: 'default',
                    backgroundColor: '',
                    position: 'relative',
                  }}
                >
                  <td>{(row.role_name || row.role_id || '').replace(/_/g, ' ')}</td>
                  <td>{row.full_name}</td>

                  <td style={{ 
                    color: ((row.status || '').charAt(0).toUpperCase() + (row.status || '').slice(1)) === 'Inactive' ? 'red' : 'green',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}>
                    {(row.status || '').charAt(0).toUpperCase() + (row.status || '').slice(1)}
                  </td>
                  <td>
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '4px' : '8px',
                      alignItems: isMobile ? 'stretch' : 'center'
                    }}>
                      <button
                        onClick={() => {
                          setViewUser(row);
                          setIsViewUserModalOpen(true);
                        }}
                        style={{
                          padding: isMobile ? '6px 8px' : '6px 12px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: isMobile ? 11 : 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          width: isMobile ? '100%' : 'auto'
                        }}
                      >
                        <MdVisibility size={14} />
                        View
                      </button>

                      {/* Only show Edit button for field officers */}
                      {(row.role_name || '').toLowerCase().includes('field') && (
                        <button
                          onClick={() => {
                            setEditUser(row);
                            setIsEditUserModalOpen(true);
                          }}
                          style={{
                            padding: isMobile ? '6px 8px' : '6px 12px',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: isMobile ? 11 : 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          <MdEdit size={14} />
                          {isMobile ? 'Edit' : 'Edit Status'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableLoadingWrapper>

      {/* Pagination */}
      {pageCount > 1 && (
        <ReactPaginate
          previousLabel={'previous'}
          nextLabel={'next'}
          breakLabel={'...'}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={'pagination'}
          activeClassName={'active'}
        />
      )}

      {/* Regional Info Notice */}
      <div style={{
        marginTop: 20,
        padding: 15,
        backgroundColor: '#e3f2fd',
        borderRadius: 8,
        border: '1px solid #bbdefb',
        fontSize: 14,
        color: '#1976d2'
      }}>
        <strong>User Management:</strong> This view shows only users within your MMDA region.
        As an MMDA admin, you can create Field Officer accounts, view user details and activity.
      </div>
    </>
  );
};

export default UsersView;
